'use client';
import { useColorScheme } from '#pplx/frontend/aether/hooks/useColorScheme.js';
import { upsellViewedEvent } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { handleExternalUrl } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/handleExternalUrl.js';
import { useHomepageUpsellsQuery } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useHomepageUpsellsQuery.js';
import { usePageBottomUpsellAction } from '#pplx/frontend/apps/ask/upsell-slots/comet-ntp-bottom/internal/usePageBottomUpsellAction.js';
import { buildUpsellSlotAnalytics } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useState } from 'react';
// EXTERNAL_URL is scoped to this slot only (same pattern as the banner slot) —
// destinations here can be ConfigBoard-controlled absolute URLs, and
// handleExternalUrl enforces the http(s) allowlist and noopener that the
// generic PERMALINK path does not.
const PAGE_BOTTOM_CUSTOM_HANDLERS = {
    EXTERNAL_URL: handleExternalUrl,
};
const PAGE_BOTTOM_EXTRA_SUPPORTED_CTAS = Object.keys(PAGE_BOTTOM_CUSTOM_HANDLERS);
const isPageBottomCardUpsellRequestEnabled = (input) => input.callsite.enabled !== false &&
    input.callsite.surface?.isAllowedFloatingPath !== false &&
    input.callsite.surface?.isMobileLayout !== true &&
    input.callsite.surface?.canShowInViewport !== false &&
    input.callsite.surface?.hasPrimarySurfaceContent !== true;
export const resolvePageBottomCardUpsellSlotPlan = ({ input, data, controls, }) => {
    const enabled = input.callsite.enabled !== false && input.callsite.surface?.isAllowedFloatingPath !== false;
    if (!enabled) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (input.callsite.surface?.isMobileLayout ||
        input.callsite.surface?.canShowInViewport === false) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    if (input.callsite.surface?.hasPrimarySurfaceContent) {
        return { status: 'blocked', show: false, reason: 'competing_surface' };
    }
    if (data.isLoading) {
        return { status: 'loading', show: false, reason: 'fetch_loading', isLoading: true };
    }
    if (data.isError) {
        return { status: 'error', show: false, reason: 'fetch_error', isError: true };
    }
    const { upsellInformation } = data;
    if (!upsellInformation) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!upsellInformation.app_location ||
        !input.acceptedLocations.includes(upsellInformation.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    if (upsellInformation.cta &&
        !isCtaSupported(upsellInformation.cta, PAGE_BOTTOM_EXTRA_SUPPORTED_CTAS)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (data.sessionDismissed) {
        return { status: 'blocked', show: false, reason: 'dismissed' };
    }
    if (!upsellInformation.title ||
        !upsellInformation.button_text ||
        !upsellInformation.image_asset) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    if (!data.imageAsset) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation,
        render: {
            kind: 'page_bottom_card',
            componentKey: 'PageBottomCard',
            props: {
                imageAsset: data.imageAsset,
                onClick: controls.onClick,
                onDismiss: controls.onDismiss,
                upsellInformation,
            },
        },
        actions: controls,
        analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
    };
};
export const usePageBottomCardUpsellSlotPlan = ({ input, }) => {
    const enabled = isPageBottomCardUpsellRequestEnabled(input);
    const { productBanner, isError, isLoading } = useHomepageUpsellsQuery({
        enabled,
        location: input.source.request.location,
    });
    const { impression } = useInstrumentation();
    const { colorScheme } = useColorScheme();
    const [sessionDismissed, setSessionDismissed] = useState(false);
    const hideUpsell = useCallback(() => setSessionDismissed(true), []);
    const handleUpsell = usePageBottomUpsellAction({
        upsellInformation: productBanner || undefined,
        options: { customHandlers: PAGE_BOTTOM_CUSTOM_HANDLERS },
    });
    const dismissUpsell = usePageBottomUpsellAction({
        upsellInformation: productBanner || undefined,
        options: { hideUpsell, interactionType: 'dismiss' },
    });
    const onClick = useCallback(() => {
        if (!productBanner)
            return;
        void handleUpsell();
    }, [handleUpsell, productBanner]);
    const onDismiss = useCallback(() => {
        void dismissUpsell();
    }, [dismissUpsell]);
    const imageAsset = colorScheme === 'dark' && productBanner?.image_asset_dark
        ? productBanner.image_asset_dark
        : productBanner?.image_asset;
    const plan = resolvePageBottomCardUpsellSlotPlan({
        input,
        data: {
            imageAsset,
            isError,
            isLoading,
            sessionDismissed,
            upsellInformation: productBanner,
        },
        controls: {
            onClick,
            onDismiss,
        },
    });
    const readyUpsellInformation = plan.show ? plan.upsellInformation : null;
    useEffect(() => {
        if (!readyUpsellInformation?.app_location) {
            return;
        }
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellLocation: readyUpsellInformation.app_location,
                upsellUUID: readyUpsellInformation.upsell_uuid ?? '',
                upsellName: readyUpsellInformation.name ?? '',
                upsellType: readyUpsellInformation.upsell_type ?? '',
                cta: readyUpsellInformation.cta,
            },
        }));
    }, [
        readyUpsellInformation,
        readyUpsellInformation?.app_location,
        readyUpsellInformation?.cta,
        readyUpsellInformation?.name,
        readyUpsellInformation?.upsell_type,
        readyUpsellInformation?.upsell_uuid,
        impression,
    ]);
    return plan;
};
//# sourceMappingURL=usePageBottomCardUpsellSlotPlan.js.map